import { Inject, Injectable } from '@angular/core';
import { isNaN } from 'lodash';

import { NgRedux } from '@angular-redux/store';

import { interval, Observable, Subscription } from 'rxjs';

import { RealtimeActions } from './realtime.actions';
import { RealtimePartialStoreState } from './realtime.reducer';
import * as RealtimeSelectors from './realtime.selectors';
import { BusLocation, StopCode, StructuredBusLocation } from '../model/realtime.models';
import { filter, finalize, map } from 'rxjs/operators';
import { DateBuilder, TimeUtil, WebSocketService } from '@ridango/core';
import Env from '@ridango/core/env';

@Injectable({ providedIn: 'root' })
export class RealtimeFacade {
    private connectedSubscription: Subscription | null = null;

    vehicleLocations$ = this.webSocket.connect(this.env.REALTIME_VEHICLE_STATUS_PATH).pipe(
        map((r) => this.mapLocations(r)),
        filter((r) => !!r),
        map((r) => Object.values(r)),
        finalize(() => {
            this.webSocket.closeConnection();
        })
    );

    locations$: Observable<BusLocation[]> = this.store
        .select(RealtimeSelectors.getLocations)
        .pipe(map((location) => Object.values(location ?? {})));
    selectedLocation$: Observable<any> = this.store.select(RealtimeSelectors.getSelectedLocations);
    shapes$: Observable<any[]> = this.store.select(RealtimeSelectors.getShapes);

    private locationExpiringInSeconds: number;
    private locationClearingIntervalInSeconds: number;
    private scheduler: Observable<number>;

    constructor(
        private store: NgRedux<RealtimePartialStoreState>,
        private actions: RealtimeActions,
        @Inject('ENV') private env: Env,
        private webSocket: WebSocketService
    ) {
        this.setLocationExpiringAndClearingInterval();
        this.scheduler = interval(TimeUtil.getMillisecondsFromSeconds(this.locationClearingIntervalInSeconds));
        // Send the region payload once per connection open, including after reconnects.
        this.connectedSubscription = this.webSocket.connected$.subscribe(() => this.startWebsocket());
    }

    private setLocationExpiringAndClearingInterval(): void {
        const locationExpiring = this.env.BUS_LOCATION_EXPIRING_IN_SECONDS;
        this.locationExpiringInSeconds = locationExpiring != undefined ? locationExpiring : 60;
        const locationClearing = this.env.BUS_LOCATION_CLEARING_INTERVAL_IN_SECONDS;
        this.locationClearingIntervalInSeconds = locationClearing != undefined ? locationClearing : 10;
    }

    fetchShapes(tripId: number) {
        this.store.dispatch(this.actions.fetchShapes(tripId));
    }

    startWebsocket() {
        this.webSocket.send({
            regionId: this.env.REALTIME_REGION_ID || this.env.REGION_ID,
            topLeftCoordinates: {
                longitude: 0,
                latitude: 180
            },
            bottomRightCoordinates: {
                longitude: 180,
                latitude: -180
            }
        });
    }

    private mapLocations(locations: BusLocation[] | StructuredBusLocation[]) {
        let flatBusLocations: BusLocation[] = [];
        if (Array.isArray(locations) && locations.length > 0) {
            if ('trip' in locations[0]) {
                // Convert structured to flat bus locations
                flatBusLocations = (locations as StructuredBusLocation[]).map(this.convertStructuredToFlatBusLocation);
            } else {
                flatBusLocations = locations as BusLocation[];
            }
        } else {
            return;
        }

        const expiringTime = DateBuilder.now().addSeconds(this.locationExpiringInSeconds).getDate();

        //from PubTrans we receive device_id as null, so index used as a key
        return flatBusLocations.reduce((accVal, location, index) => {
            location.expiring_time = expiringTime;
            const key = isNaN(location.device_id) ? `index_${index}` : location.device_id;
            accVal[key] = location;
            return accVal;
        }, {});
    }

    setLocations(locations: BusLocation[] | StructuredBusLocation[]) {
        this.store.dispatch(this.actions.setLocations(this.mapLocations(locations)));
    }

    convertStructuredToFlatBusLocation(structured: StructuredBusLocation): BusLocation {
        // Note: It returns differnet data.
        // UpdateTime == true returns date of month eg. Today is 06.03.2025, so it will return 6
        // UpdateTime == False returns current time in ms.
        const timestamp = structured.updateTime ? new Date(structured.updateTime).getDate() : Date.now();
        const busLocation: BusLocation = {
            device_id: parseInt(structured.deviceId),
            latitude: structured.position.latitude,
            longitude: structured.position.longitude,
            bus_speed: structured.position.speed,
            bearing: structured.position.bearing,
            icon: structured.trip?.icon,
            region_id: structured.regionId,
            location_id: structured.locationId,
            direction_id: structured.trip?.directionId?.toString(),
            route_short_name: structured.trip?.routeShortName ?? '',
            timestamp: timestamp
        };
        if (structured.trip && structured.trip.tripId) {
            busLocation.trip_id = structured.trip.tripId;
        }
        if (structured.trip?.routeId) {
            busLocation.routeId = structured.trip.routeId;
        }
        return busLocation;
    }
    setSelectedLocation(location) {
        this.store.dispatch(this.actions.setSelectedLocation(location));
    }

    setShapes(shapes: any[]) {
        this.store.dispatch(this.actions.setShapes(shapes));
    }

    clearExpiredLocations() {
        this.store.dispatch(this.actions.clearExpiredLocations());
    }

    getScheduler(): Observable<number> {
        return this.scheduler;
    }

    setRealtimeSchedule(payload: StopCode) {
        this.store.dispatch(this.actions.setRealtimeSchedule(payload));
    }

    clearRealtimeSchedule() {
        this.store.dispatch(this.actions.clearRealtimeSchedule());
    }
}
